import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Group,
  Button,
  TextInput,
  Loader,
  Modal,
  Text,
  Badge,
  Select,
  Stack,
  CloseButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconUserPlus, IconSearch, IconFilter, IconRefresh, IconTrash, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { UserTable } from './UserTable';
import { UserForm } from './UserForm';
import { User } from '../../../types';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
  const [userModalOpened, { open: openUserModal, close: closeUserModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const { token, user: currentUser } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  // TEMPORARY: Allow everyone to create, edit, delete users
  // Remove this when role-based permissions are fixed
  const canCreateUser = true;
  const canEditUser = true;
  const canDeleteUser = true;
  const canChangeStatus = true;

  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token]);

  const fetchUsers = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get<User[]>(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        notifications.show({
          title: 'Access Denied',
          message: 'You do not have permission to view users',
          color: 'red',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to fetch users',
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search, role, and status
  const filteredUsers = users.filter(user => {
    // Search filter
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phoneNumber?.includes(searchQuery);
    
    // Role filter
   // Role filter - compare as strings
const matchesRole = !selectedRoleFilter || 
  user.roles?.some(role => role === selectedRoleFilter);
    
    // Status filter
    const matchesStatus = !selectedStatusFilter ||
      (selectedStatusFilter === 'active' && user.enabled) ||
      (selectedStatusFilter === 'locked' && !user.enabled);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditing(true);
    openUserModal();
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    openDeleteModal();
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser || !token) return;
    
    // Prevent self-deletion
    if (selectedUser.id === currentUser?.id) {
      notifications.show({
        title: 'Error',
        message: 'You cannot delete your own account',
        color: 'red',
      });
      closeDeleteModal();
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      notifications.show({
        title: 'Success',
        message: 'User deleted successfully',
        color: 'green',
      });
      
      fetchUsers();
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting user:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete user',
        color: 'red',
      });
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    // Prevent self-lock
    if (user.id === currentUser?.id) {
      notifications.show({
        title: 'Error',
        message: 'You cannot lock/unlock your own account',
        color: 'red',
      });
      return;
    }
    
    if (!token) return;
    
    try {
      await axios.patch(
        `${API_URL}/users/${user.id}/toggle-status?enabled=${!user.enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      notifications.show({
        title: 'Success',
        message: `User ${user.enabled ? 'locked' : 'unlocked'} successfully`,
        color: 'green',
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update user status',
        color: 'red',
      });
    }
  };

  const handleSuccess = () => {
    fetchUsers();
    closeUserModal();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedRoleFilter(null);
    setSelectedStatusFilter(null);
  };

  // Role filter options with ROLE_ prefix
  const roleOptions = [
    { value: 'ROLE_ADMIN', label: 'Admin' },
    { value: 'ROLE_DIRECTOR', label: 'Director' },
    { value: 'ROLE_MANAGER', label: 'Manager' },
    { value: 'ROLE_USER', label: 'User' },
  ];

  // Status filter options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'locked', label: 'Locked' },
  ];

  if (!token) {
    return (
      <Container size="xl" py={50}>
        <Paper withBorder p="xl" radius="md">
          <Text ta="center" size="lg" c="dimmed">
            Please log in to access user management.
          </Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>User Management</Title>
          <Text size="sm" c="dimmed" mt={5}>
            Manage users and their permissions
          </Text>
        </div>
        {canCreateUser && (
          <Button
            leftSection={<IconUserPlus size={16} />}
            color="exportGreen"
            onClick={() => {
              setSelectedUser(null);
              setIsEditing(false);
              openUserModal();
            }}
          >
            Create New User
          </Button>
        )}
      </Group>

      {/* Filters Section */}
      <Paper withBorder p="md" mb="lg">
        <Group align="flex-end">
          <TextInput
            placeholder="Search by name, email, phone..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
            size="md"
          />
          <Select
            placeholder="Filter by role"
            leftSection={<IconFilter size={16} />}
            value={selectedRoleFilter}
            onChange={setSelectedRoleFilter}
            data={roleOptions}
            clearable
            size="md"
            w={150}
          />
          <Select
            placeholder="Filter by status"
            value={selectedStatusFilter}
            onChange={setSelectedStatusFilter}
            data={statusOptions}
            clearable
            size="md"
            w={150}
          />
          <Button 
            variant="light" 
            leftSection={<IconRefresh size={16} />} 
            onClick={fetchUsers}
            size="md"
          >
            Refresh
          </Button>
        </Group>
        
        {/* Active Filters Display */}
        {(selectedRoleFilter || selectedStatusFilter || searchQuery) && (
          <Group mt="md" gap="xs">
            <Text size="xs" c="dimmed">Active filters:</Text>
            {searchQuery && (
              <Badge 
                variant="light"
                rightSection={
                  <CloseButton
                    size="xs"
                    onClick={() => setSearchQuery('')}
                    style={{ marginLeft: 4 }}
                  />
                }
              >
                Search: {searchQuery}
              </Badge>
            )}
            {selectedRoleFilter && (
              <Badge 
                variant="light" 
                color="blue"
                rightSection={
                  <CloseButton
                    size="xs"
                    onClick={() => setSelectedRoleFilter(null)}
                    style={{ marginLeft: 4 }}
                  />
                }
              >
                Role: {roleOptions.find(r => r.value === selectedRoleFilter)?.label}
              </Badge>
            )}
            {selectedStatusFilter && (
              <Badge 
                variant="light" 
                color={selectedStatusFilter === 'active' ? 'green' : 'orange'}
                rightSection={
                  <CloseButton
                    size="xs"
                    onClick={() => setSelectedStatusFilter(null)}
                    style={{ marginLeft: 4 }}
                  />
                }
              >
                Status: {selectedStatusFilter === 'active' ? 'Active' : 'Locked'}
              </Badge>
            )}
            <Button
              variant="subtle"
              size="xs"
              onClick={clearAllFilters}
              leftSection={<IconX size={12} />}
              color="gray"
            >
              Clear all
            </Button>
          </Group>
        )}
      </Paper>

      {/* Users Table */}
      <Paper withBorder radius="md">
        {loading ? (
          <div className="flex justify-center items-center p-12">
            <Loader size="lg" />
          </div>
        ) : (
          <UserTable
            users={filteredUsers}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onToggleStatus={handleToggleUserStatus}
            canEdit={canEditUser}
            canDelete={canDeleteUser}
            canChangeStatus={canChangeStatus}
            currentUserId={currentUser?.id}
          />
        )}
      </Paper>

      {/* Create/Edit Modal */}
      <Modal
        opened={userModalOpened}
        onClose={closeUserModal}
        title={isEditing ? 'Edit User' : 'Create New User'}
        size="lg"
        centered
      >
        <UserForm
          user={selectedUser}
          isEditing={isEditing}
          token={token}
          onSuccess={handleSuccess}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title="Confirm Deletion"
        size="md"
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to delete user{' '}
            <Text component="span" fw={600} c="red">
              {selectedUser?.fullName}
            </Text>
            ?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. All data associated with this user will be permanently removed.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
            <Button 
              color="red" 
              onClick={confirmDeleteUser}
              leftSection={<IconTrash size={16} />}
            >
              Delete User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};